import { computed } from 'vue'
import { useDroneEntity } from '@/composables/Drone/useDroneEntity'
import { CesiumService } from '@/handlers/cesium.service'
import { Color, Cartesian2, LabelStyle, VerticalOrigin } from 'cesium'
import { switchMapStyle } from '@/handlers/shared'
import useUserSettings from '@/composables/useUserSettings'

const viewer = computed(() => CesiumService.viewer.value)
export const ADMIN_ORGANISATION_ID = '52afdbe6-daf5-40e2-b1b0-8923c1cacabc'
export const adminAccess = [
  '3662a234-d0a1-70a0-1f8d-746ee2a6ebd3', //divine
  '56d23284-2071-7024-f810-62b400ccdde6', //emma
  '66f2a2d4-c021-7051-ce58-0c9bd571d6bc', //fidel
  '86a232a4-c081-7099-dd4f-8d7168cd4809', //test user2
  'c6d202a4-40d1-70e2-121a-893c61542cd6', //rob
  'c642d214-4081-7034-5604-cf1a34fcfe4e', //peter
]
export const labelStyle = {
  active: {
    font: '12px sans-serif',
    fillColor: Color.fromCssColorString('#fff'),
    style: LabelStyle.FILL,
    verticalOrigin: VerticalOrigin.BOTTOM,
    pixelOffset: new Cartesian2(90, 0),
    backgroundColor: Color.fromCssColorString('#056473'),
    backgroundPadding: new Cartesian2(7, 5),
    showBackground: true,
  },
  inactive: {
    font: '12px sans-serif',
    fillColor: Color.fromCssColorString('#ffffff80'),
    style: LabelStyle.FILL,
    verticalOrigin: VerticalOrigin.BOTTOM,
    pixelOffset: new Cartesian2(90, 0),
    backgroundColor: Color.fromCssColorString('#2caec340'),
    backgroundPadding: new Cartesian2(7, 5),
    showBackground: true,
  },
}
export type AppMode = 'FLY_MODE' | 'PLANNER_MODE' | 'ANALYTICS' | 'FLIGHT_STORY'
export const modeSelect: { name: string; id: AppMode; isDevices: boolean }[] = [
  {
    name: 'Fly Mode',
    id: 'FLY_MODE',
    isDevices: false,
  },
  {
    name: 'Planner Mode',
    id: 'PLANNER_MODE',
    isDevices: false,
  },
  {
    name: 'Analytics',
    id: 'ANALYTICS',
    isDevices: false,
  },
  {
    name: 'Flight Story',
    id: 'FLIGHT_STORY',
    isDevices: false,
  },
]
export const mapViews = computed(() => {
  const { settings } = useUserSettings()
  return [
    {
      name: 'Google 2D',
      img: '/images/layers/google-2d.png',
      url: 'google-maps',
      active: settings.value.mapStyle === 'google-maps',
    },
    {
      name: 'Satellite',
      img: '/images/layers/satellite.png',
      url: 'satellite-v9',
      active: settings.value.mapStyle === 'satellite-v9',
    },
    {
      name: 'Satellite Street',
      img: '/images/layers/satellite-street.png',
      url: 'satellite-streets-v12',
      active: settings.value.mapStyle === 'satellite-streets-v12',
    },
    {
      name: 'Street',
      img: '/images/layers/street.png',
      url: 'streets-v12',
      active: settings.value.mapStyle === 'streets-v12',
    },
    {
      name: 'Navigation - Night',
      img: '/images/layers/navigation-night.png',
      url: 'navigation-night-v1',
      active: settings.value.mapStyle === 'navigation-night-v1',
    },
    {
      name: 'Navigation - Day',
      img: '/images/layers/navigation-day.png',
      url: 'navigation-day-v1',
      active: settings.value.mapStyle === 'navigation-day-v1',
    },
    {
      name: 'Street - Mono',
      img: '/images/layers/street-mono.png',
      url: 'outdoors-v12',
      active: settings.value.mapStyle === 'outdoors-v12',
    },
  ]
})
export const changeMapStyle = async (url: string) => {
  const { settings, saveSettings } = useUserSettings()
  const { fpvViewer } = useDroneEntity()

  // If clicking on the current style, do nothing (already active)
  if (settings.value.mapStyle === url) {
    return
  }

  // Save the previous URL in case we need to rollback
  const prev = settings.value.mapStyle

  try {
    if (viewer.value) {
      await switchMapStyle(viewer.value, url)
    }
    if (fpvViewer.value) {
      await switchMapStyle(fpvViewer.value, url)
    }
    // Only update the store after all switches succeed
    saveSettings({ mapStyle: url })
  } catch (error) {
    // Revert to previous URL on failure
    saveSettings({ mapStyle: prev })
    console.error('Error switching map style:', error)
  }
}
export const appIntegrations = [
  {
    name: 'Volant',
    value: 'volant',
    component: 'Volant',
  },
  {
    name: 'Drone Defense',
    value: 'drone_defense',
    component: 'DroneDefense',
  },
]
export const languages = [
  { code: 'aa', name: 'Afar' },
  { code: 'ab', name: 'Abkhazian' },
  { code: 'af', name: 'Afrikaans' },
  { code: 'ak', name: 'Akan' },
  { code: 'am', name: 'Amharic' },
  { code: 'ar', name: 'Arabic' },
  { code: 'as', name: 'n/a' },
  { code: 'av', name: 'Avaric' },
  { code: 'ay', name: 'Aymara' },
  { code: 'az', name: 'Azerbaijani' },
  { code: 'ba', name: 'Bashkir' },
  { code: 'be', name: 'Belarusian' },
  { code: 'bg', name: 'Bulgarian' },
  { code: 'bi', name: 'Bislama' },
  { code: 'bm', name: 'Bambara' },
  { code: 'bn', name: 'Bengali' },
  { code: 'bo', name: 'Tibetan' },
  { code: 'br', name: 'Breton' },
  { code: 'bs', name: 'Bosnian' },
  { code: 'ca', name: 'Catalan' },
  { code: 'ce', name: 'Chechen' },
  { code: 'ch', name: 'Chamorro' },
  { code: 'co', name: 'Corsican' },
  { code: 'cr', name: 'Cree' },
  { code: 'cs', name: 'Czech' },
  { code: 'cu', name: 'Church Slavic' },
  { code: 'cv', name: 'Chuvash' },
  { code: 'cy', name: 'Welsh' },
  { code: 'da', name: 'Danish' },
  { code: 'de', name: 'German' },
  { code: 'dv', name: 'Divehi' },
  { code: 'dz', name: 'Dzongkha' },
  { code: 'ee', name: 'Ewe' },
  { code: 'el', name: 'Greek' },
  { code: 'en', name: 'English' },
  { code: 'eo', name: 'Esperanto' },
  { code: 'es', name: 'Spanish' },
  { code: 'et', name: 'Estonian' },
  { code: 'eu', name: 'Basque' },
  { code: 'fa', name: 'Persian' },
  { code: 'ff', name: 'Fulah' },
  { code: 'fi', name: 'Finnish' },
  { code: 'fj', name: 'Fijian' },
  { code: 'fo', name: 'Faroese' },
  { code: 'fr', name: 'French' },
  { code: 'fy', name: 'Western Frisian' },
  { code: 'ga', name: 'Irish' },
  { code: 'gd', name: 'Scottish Gaelic' },
  { code: 'gl', name: 'Galician' },
  { code: 'gn', name: 'Guarani' },
  { code: 'gu', name: 'Gujarati' },
  { code: 'gv', name: 'Manx' },
  { code: 'ha', name: 'Hausa' },
  { code: 'he', name: 'Hebrew' },
  { code: 'hi', name: 'Hindi' },
  { code: 'ho', name: 'Hiri Motu' },
  { code: 'hr', name: 'Croatian' },
  { code: 'ht', name: 'Haitian Creole' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'hy', name: 'Armenian' },
  { code: 'hz', name: 'Herero' },
  { code: 'ia', name: 'Interlingua' },
  { code: 'id', name: 'Indonesian' },
  { code: 'ie', name: 'Interlingue' },
  { code: 'ig', name: 'Igbo' },
  { code: 'ii', name: 'Sichuan Yi' },
  { code: 'ik', name: 'Inupiaq' },
  { code: 'io', name: 'Ido' },
  { code: 'is', name: 'Icelandic' },
  { code: 'it', name: 'Italian' },
  { code: 'iu', name: 'Inuktitut' },
  { code: 'ja', name: 'Japanese' },
  { code: 'jv', name: 'Javanese' },
  { code: 'ka', name: 'Georgian' },
  { code: 'kg', name: 'Kongo' },
  { code: 'ki', name: 'Kikuyu' },
  { code: 'kj', name: 'Kwanyama' },
  { code: 'kk', name: 'Kazakh' },
  { code: 'kl', name: 'Kalaallisut' },
  { code: 'km', name: 'Khmer' },
  { code: 'kn', name: 'Kannada' },
  { code: 'ko', name: 'Korean' },
  { code: 'kr', name: 'Kanuri' },
  { code: 'ks', name: 'Kashmiri' },
  { code: 'ku', name: 'Kurdish' },
  { code: 'kv', name: 'Komi' },
  { code: 'kw', name: 'Cornish' },
  { code: 'ky', name: 'Kyrgyz' },
  { code: 'la', name: 'Latin' },
  { code: 'lb', name: 'Luxembourgish' },
  { code: 'lg', name: 'Ganda' },
  { code: 'li', name: 'Limburgish' },
  { code: 'ln', name: 'Lingala' },
  { code: 'lo', name: 'Lao' },
  { code: 'lt', name: 'Lithuanian' },
  { code: 'lu', name: 'Luba-Katanga' },
  { code: 'lv', name: 'Latvian' },
  { code: 'mg', name: 'Malagasy' },
  { code: 'mh', name: 'n/a' },
  { code: 'mi', name: 'Māori' },
  { code: 'mk', name: 'Macedonian' },
  { code: 'ml', name: 'Malayalam' },
  { code: 'mn', name: 'Mongolian' },
  { code: 'mr', name: 'Marathi' },
  { code: 'ms', name: 'Malay' },
  { code: 'mt', name: 'Maltese' },
  { code: 'my', name: 'Burmese' },
  { code: 'na', name: 'Nauru' },
  { code: 'nb', name: 'Norwegian Bokmål' },
  { code: 'nd', name: 'North Ndebele' },
  { code: 'ne', name: 'Nepali' },
  { code: 'ng', name: 'Ndonga' },
  { code: 'nl', name: 'Dutch' },
  { code: 'nn', name: 'Norwegian Nynorsk' },
  { code: 'no', name: 'Norwegian' },
  { code: 'nr', name: 'South Ndebele' },
  { code: 'nv', name: 'Navajo' },
  { code: 'ny', name: 'Chichewa' },
  { code: 'oc', name: 'Occitan' },
  { code: 'oj', name: 'Ojibwa' },
  { code: 'om', name: 'Oromo' },
  { code: 'or', name: 'Oriya' },
  { code: 'os', name: 'Ossetian' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'pi', name: 'Pali' },
  { code: 'pl', name: 'Polish' },
  { code: 'ps', name: 'Pashto' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'qu', name: 'Quechua' },
  { code: 'rm', name: 'Romansh' },
  { code: 'rn', name: 'Rundi' },
  { code: 'ro', name: 'Romanian' },
  { code: 'ru', name: 'Russian' },
  { code: 'rw', name: 'Kinyarwanda' },
  { code: 'sa', name: 'Sanskrit' },
  { code: 'sc', name: 'Sardinian' },
  { code: 'sd', name: 'Sindhi' },
  { code: 'se', name: 'Northern Sami' },
  { code: 'sg', name: 'Sango' },
  { code: 'si', name: 'Sinhala' },
  { code: 'sk', name: 'Slovak' },
  { code: 'sl', name: 'Slovenian' },
  { code: 'sm', name: 'Samoan' },
  { code: 'sn', name: 'Shona' },
  { code: 'so', name: 'Somali' },
  { code: 'sq', name: 'Albanian' },
  { code: 'sr', name: 'Serbian' },
  { code: 'ss', name: 'Swati' },
  { code: 'st', name: 'Southern Sotho' },
  { code: 'su', name: 'Sundanese' },
  { code: 'sv', name: 'Swedish' },
  { code: 'sw', name: 'Swahili' },
  { code: 'ta', name: 'Tamil' },
  { code: 'te', name: 'Telugu' },
  { code: 'tg', name: 'Tajik' },
  { code: 'th', name: 'Thai' },
  { code: 'ti', name: 'Tigrinya' },
  { code: 'tk', name: 'Turkmen' },
  { code: 'tl', name: 'Tagalog' },
  { code: 'tn', name: 'Tswana' },
  { code: 'to', name: 'Tonga' },
  { code: 'tr', name: 'Turkish' },
  { code: 'ts', name: 'Tsonga' },
  { code: 'tt', name: 'Tatar' },
  { code: 'tw', name: 'Twi' },
  { code: 'ty', name: 'Tahitian' },
  { code: 'ug', name: 'Uighur' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'ur', name: 'Urdu' },
  { code: 'uz', name: 'Uzbek' },
  { code: 've', name: 'Venda' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'vo', name: 'Volapük' },
  { code: 'wa', name: 'Walloon' },
  { code: 'wo', name: 'Wolof' },
  { code: 'xh', name: 'Xhosa' },
  { code: 'yi', name: 'Yiddish' },
  { code: 'yo', name: 'Yoruba' },
  { code: 'za', name: 'Zhuang' },
  { code: 'zh', name: 'Chinese' },
  { code: 'zu', name: 'Zulu' },
]
